import React, { useState } from 'react';
import Table, { TableColumn } from '../../components/common/Table';
import { InvoiceDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { formatDateForInput } from '../../utils/dateUtils/formatDateForInput';
import { getBadgeColors } from '../../utils/displayUtils';
import Badge from '../common/Badge';
import createInvoiceActions from '../actionMenus/InvoiceActions';
import Drawer from '../common/Drawer';
import InvoiceForm from '../forms/InvoiceForm';
import { useInvoiceApi } from '../../hooks/useInvoiceApi';
import { usePaymentApi } from '../../hooks/usePaymentApi';
import { isInvoiceSelectableForNetcash } from '../../utils/invoiceEligibility';

interface InvoiceTableProps {
  data: InvoiceDTO[];
  showEditForPaymentAction?: boolean;
  batchMonth?: number;
  batchYear?: number;
  onRefresh?: () => Promise<void>;
  handleEdit?: (row: InvoiceDTO) => void;
  handleDelete?: (row: InvoiceDTO) => void;
  handleViewMember?: (row: InvoiceDTO) => void;
  handleLoginAsMember?: (row: InvoiceDTO) => void;
  handleViewDocument?: (row: InvoiceDTO) => void;
  handleRevertInvoice?: (row: InvoiceDTO) => void;
  handleResend?: (row: InvoiceDTO) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  onSort?: (sortKey: string) => void;
  selectedIds?: Set<string>;
  onSelectRow?: (id: string, checked: boolean) => void;
  onSelectAll?: (checked: boolean) => void;
  // Invoices already committed to a Netcash batch, keyed by lowercase invoice id -> why. Not selectable.
  netcashBlocked?: Record<string, string>;
}

const InvoiceTable: React.FC<InvoiceTableProps> = ({
  data,
  showEditForPaymentAction = false,
  batchMonth,
  batchYear,
  onRefresh,
  handleEdit,
  handleDelete,
  handleViewMember,
  handleLoginAsMember,
  handleViewDocument,
  handleRevertInvoice,
  handleResend,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
  sortBy,
  sortDescending,
  onSort,
  selectedIds = new Set(),
  onSelectRow,
  onSelectAll,
  netcashBlocked = {},
}) => {
  const { getById: getInvoiceById, edit: editInvoice } = useInvoiceApi();
  const { processEftPayment } = usePaymentApi();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDTO | null>(null);
  const [memberPaidUpTo, setMemberPaidUpTo] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEditForPayment = async (invoice: InvoiceDTO) => {
    try {
      const response = await getInvoiceById(invoice.id || '');
      if (response?.data) {
        // Pre-populate amount paid with total amount and date paid with today's date
        const invoiceWithDefaults = {
          ...response.data,
          totalPaid: response.data.totalIncl || 0,
          paidDate: formatDateForInput(new Date()) as any
        };
        setSelectedInvoice(invoiceWithDefaults);

        // Calculate default paid up to: last day of batch month, one year after batch year
        let defaultPaidUpTo = '';
        if (batchMonth && batchYear) {
          const nextYear = batchYear + 1;
          // JavaScript Date months are 0-indexed, so batchMonth (1-12) needs adjustment
          const lastDayOfBatchMonthNextYear = new Date(nextYear, batchMonth, 0); // Day 0 of next month = last day of current month
          defaultPaidUpTo = formatDateForInput(lastDayOfBatchMonthNextYear);
        } else {
          // Fallback to current logic if batch info not available
          const today = new Date();
          const nextYear = today.getFullYear() + 1;
          const thisMonth = today.getMonth();
          const lastDayOfMonthNextYear = new Date(nextYear, thisMonth + 1, 0);
          defaultPaidUpTo = formatDateForInput(lastDayOfMonthNextYear);
        }

        // Set the default value (override with current member paidUpTo if exists)
        setMemberPaidUpTo(defaultPaidUpTo);

        setIsDrawerOpen(true);
      }
    } catch (error) {
      console.error('Error fetching invoice for payment edit:', error);
    }
  };

  const blockedReason = (row: InvoiceDTO) => (row.id ? netcashBlocked[row.id.toLowerCase()] : undefined);
  const isRowSelectable = (row: InvoiceDTO) => isInvoiceSelectableForNetcash(row) && !blockedReason(row);

  const selectableCurrentPageIds = data
    .filter(isRowSelectable)
    .map(row => row.id)
    .filter((id): id is string => !!id);
  const allSelected = selectableCurrentPageIds.length > 0 && selectableCurrentPageIds.every(id => selectedIds.has(id));
  const someSelected = selectableCurrentPageIds.some(id => selectedIds.has(id)) && !allSelected;

  const columns: TableColumn[] = [
    ...(onSelectRow && onSelectAll
      ? [
        {
          label: (
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el: HTMLInputElement | null) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSelectAll?.(e.target.checked)}
              disabled={selectableCurrentPageIds.length === 0}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
            />
          ),
          key: 'checkbox',
          render: (row: InvoiceDTO) => {
            const selectable = isRowSelectable(row);
            return (
              <input
                type="checkbox"
                checked={selectedIds.has(row.id ?? '')}
                onChange={(e) => onSelectRow?.(row.id ?? '', e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                disabled={!selectable}
                title={selectable ? undefined : blockedReason(row) ?? 'Only unpaid sales orders can be pushed to Netcash'}
                className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 disabled:opacity-30"
              />
            );
          },
        },
      ]
      : []),
    {
      label: 'Component',
      key: 'adminComponentId',
      render: (row: InvoiceDTO) => (
        <span>{row.adminComponent?.title}</span>
      )
    },

    {
      label: 'Type',
      key: 'invoiceTypeId',
      render: (row: InvoiceDTO) => (
        <span>{row.invoiceType?.title}</span>
      )
    },

    {
      label: 'Member',
      key: 'memberId',
      sortable: true,
      sortKey: 'FirstName',
      render: (row: InvoiceDTO) => (
        <div className='flex flex-col'>
          <span>{row.member?.firstName} {row.member?.lastName}</span>
          <span>{row.member?.idPassportNumber}</span>
        </div>
      )
    },

    {
      label: 'Document',
      key: 'document',
      render: (row: InvoiceDTO) => (
        <div className='flex flex-col'>
          <span>{row.salesOrderNumber || 'No SO No'}</span>
          <span>{row.invoiceNumber || 'No Invoice No'}</span>
        </div>
      )
    },

    {
      label: 'Date Paid',
      key: 'paidDate',
      sortable: true,
      sortKey: 'PaidDate',
      format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
    },

    {
      label: 'Excl',
      key: 'totalExcl',
      render: (row: InvoiceDTO) => (
        <div className='flex flex-col'>
          <span>R {row.totalExcl?.toFixed(2)}</span>
        </div>
      )
    },

    {
      label: 'Tax',
      key: 'totalTax',
      render: (row: InvoiceDTO) => (
        <div className='flex flex-col'>
          <span>R {row.totalTax?.toFixed(2)}</span>
        </div>
      )
    },

    {
      label: 'Incl',
      key: 'totalIncl',
      sortable: true,
      sortKey: 'TotalIncl',
      render: (row: InvoiceDTO) => (
        <div className='flex flex-col'>
          <span>R {row.totalIncl?.toFixed(2)}</span>
        </div>
      )
    },

    {
      label: 'Paid',
      key: 'totalPaid',
      render: (row: InvoiceDTO) => (
        <div className='flex flex-col'>
          <span>R {row.totalPaid?.toFixed(2)}</span>
        </div>
      )
    },

    {
      label: 'Method',
      key: 'paymentMethodId',
      render: (row: InvoiceDTO) => {
        const base = row.paymentType?.colourGrading?.colour ?? '#ffffffff';
        const { bg, text } = getBadgeColors(base, 0.18, 0.4);
        return base && row.paymentType?.title ? (
          <Badge
            label={row.paymentType?.title || 'N/A'}
            variant="custom"
            customBgColor={bg}
            customTextColor={text}
          />
        ) : (
          <span>{row.paymentType?.title || 'N/A'}</span>
        );
      }
    },

    {
      label: 'Date Sent',
      key: 'invoiceSentDate',
      sortable: true,
      sortKey: 'InvoiceSentDate',
      format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
    },

    {
      label: 'Updated',
      key: 'updatedUtc',
      sortable: true,
      sortKey: 'UpdatedUtc',
      format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
    },

    ...(onSelectRow && onSelectAll
      ? [
        {
          label: 'Netcash',
          key: 'netcashBatch',
          render: (row: InvoiceDTO) => {
            const reason = blockedReason(row);
            return reason ? <Badge label={reason} variant="info" /> : <span></span>;
          },
        },
      ]
      : []),

    {
      label: 'Status',
      key: 'invoiceStatusId',
      render: (row: InvoiceDTO) => {
        const base = row.invoiceStatus?.colourGrading?.colour ?? '#ffffffff';
        const { bg, text } = getBadgeColors(base, 0.18, 0.4);
        return base && row.invoiceStatus?.title ? (
          <Badge
            label={row.invoiceStatus?.title || 'Unknown'}
            variant="custom"
            customBgColor={bg}
            customTextColor={text}
          />
        ) : (
          <span>{row.invoiceStatus?.title || 'N/A'}</span>
        );
      }
    },

    {
      label: 'Added',
      key: 'createdUtc',
      sortable: true,
      sortKey: 'CreatedUtc',
      format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        data={data}
        actions={{
          onEdit: handleEdit,
          extra: (row) =>
            createInvoiceActions({
              onViewMember: handleViewMember,
              onLoginAsMember: handleLoginAsMember,
              onViewDocument: handleViewDocument,
              onEditForPayment: showEditForPaymentAction ? handleEditForPayment : undefined,
              onRevertInvoice: handleRevertInvoice,
              onResend: handleResend,
              onDelete: handleDelete,
            })(row),
        }}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        isLoading={isLoading}
        sortBy={sortBy}
        sortDescending={sortDescending}
        onSort={onSort}
      />

      {isDrawerOpen && selectedInvoice && (
        <Drawer
          isOpen={isDrawerOpen}
          onClose={() => {
            setIsDrawerOpen(false);
            setSelectedInvoice(null);
            setMemberPaidUpTo('');
          }}
          title="Edit Invoice - Payment"
          size="large"
        >
          <InvoiceForm
            initialValues={selectedInvoice}
            showMemberPaidUpToField={true}
            memberPaidUpTo={memberPaidUpTo}
            currentMemberPaidUpTo={selectedInvoice.member?.paidUpTo}
            onMemberPaidUpToChange={setMemberPaidUpTo}
            onSubmit={async (data) => {
              try {
                setIsSubmitting(true);
                // 1. Update the invoice
                await editInvoice(selectedInvoice.id || '', data);

                // 2. Process EFT payment (mark as paid) with optional paidUpTo date
                // Pass memberId as componentId, entityType as Member, invoiceId, and paidUpTo
                // This routes to MemberService.CompleteEFTPayment which handles invoice-based payments
                await processEftPayment({
                  componentId: selectedInvoice.memberId,
                  entityType: 'Member',
                  invoiceId: selectedInvoice.id,
                  paidUpTo: memberPaidUpTo ? new Date(memberPaidUpTo) : undefined
                });

                // 3. Refresh the grid
                if (onRefresh) {
                  await onRefresh();
                }

                // 4. Close drawer
                setIsDrawerOpen(false);
                setSelectedInvoice(null);
                setMemberPaidUpTo('');
              } catch (error) {
                console.error('Error updating invoice and processing payment:', error);
              } finally {
                setIsSubmitting(false);
              }
            }}
            onCancel={() => {
              setIsDrawerOpen(false);
              setSelectedInvoice(null);
              setMemberPaidUpTo('');
            }}
            isLoading={isSubmitting}
          />
        </Drawer>
      )}
    </>
  );
};

export default InvoiceTable;
